import { Component, OnDestroy, OnInit } from '@angular/core';
import { EDatabaseRole, IContact, UserData } from 'apps/idl/src/model';
import { ContactsService, PlatformService, Store } from 'apps/idl/src/services/security';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, finalize, mergeMap, reduce, switchMap, take, takeUntil, tap, toArray } from 'rxjs/operators';
import { IAffectation } from '../../../modules/planning-rh/model/IAffectation';
import { IPlanningRH } from '../../../modules/planning-rh/model/IPlanningRH';
import { PlanningRHService } from '../../../modules/planning-rh/services/planning-rh.service';
import { PanneauChoixUserComponent } from '../../features/planning/panneau-choix-user/panneau-choix-user.component';
import { DeviceService } from '../../features/shared/services/device.service';
import { PanneauService } from '../../features/shared/services/panneau.service';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { LoaderService } from '../../features/shared/services/loader.service';
import { SnackbarService } from '../../features/shared/services/snackbar.service';
import { DateHelper, ArrayHelper } from 'apps/idl/src/helpers';

@Component({
  selector: 'di-planning',
  templateUrl: './planning.page.html',
  styleUrls: ['./planning.page.scss'],
})
export class PlanningPage extends DestroyableComponentBase implements OnInit, OnDestroy {
  public isMobileView: boolean = false;
  public users: IContact[] = [];
  public userSelected: IContact;
  public showUserName: boolean = false;
  public month1: number;
  public year1: number;
  public month2: number;
  public year2: number;
  public secteur: string = "grp_sectordefault";
  planningCalendar: Map<string, IAffectation[]> = new Map();

  months = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  private planningUpdateCompletedSubject = new Subject<void>();
  planningUpdateCompleted$ = this.planningUpdateCompletedSubject.asObservable();
  private pendingUpdatesCount = 0;

  private planningUpdateQueue$ = new Subject<{ date: Date, isWorked: boolean, groupId: string }>();

  public updatedDates = new Map<Date, boolean>();
  public currentGroupId: string;

  constructor(
    private svcDevice: DeviceService,
    private svcPanneau: PanneauService,
		private svcContact: ContactsService,
    private svcPlanningRH: PlanningRHService,
    private svcLoader: LoaderService,
    private svcPlatform : PlatformService,
    private svcStore: Store,
    private svcSnackbar: SnackbarService
  ) { 
    super();
  }

  ngOnInit() {
    this.svcDevice.isMobile$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((flag: boolean) => {
      this.isMobileView = flag;
    });
    this.initMonthYear1();
    this.initMonthYear2();
		this.initPlanningMe();

    this.planningUpdateQueue$.pipe(
      takeUntil(this.destroyed$),
      concatMap(({ date, isWorked, groupId }) => this.updatePlanningGestion(date, isWorked, groupId))
    ).subscribe();
  }

  public ngOnDestroy(): void {
    if(this.svcPlatform.isMobile && this.hasPendingUpdates()){
      this.executeAfterUpdates().subscribe();
    }
  }

  startUpdate() {
    this.pendingUpdatesCount++;
  }

  finishUpdate(): void {
    this.pendingUpdatesCount--;
    if (this.pendingUpdatesCount === 0) {
      this.planningUpdateCompletedSubject.next();
    }
  }

  hasPendingUpdates(): boolean {
    return this.pendingUpdatesCount > 0;
  }

  private initMonthYear1() {
    const currentDate = new Date();
    this.month1 = currentDate.getMonth();
    this.year1 = currentDate.getFullYear();
  }

  private initMonthYear2() {
    this.month2 = (this.month1 === 11) ? 0 : this.month1 + 1;
    this.year2 = (this.month1 === 11) ? this.year1 + 1 : this.year1;
  }

  public openPanneauUserSelect() {
    this.svcPanneau.open("Utilisateurs", PanneauChoixUserComponent, { onSelectetUser:this.setContactSelectedList });
  }

	private initPlanningMe() {
		this.svcContact.getContactFromUserId(UserData.current._id).pipe(
			tap((user: IContact) => {
				this.userSelected = user;
				this.showUserName = true;
			}),
			mergeMap(() => this.getCalendarPlanning(this.month1, this.year1, this.month2, this.year2))
		).subscribe((planningCalendar) => {
			this.planningCalendar = planningCalendar;
		});
	}

  calendarItemClick = (date: Date, isWorked: boolean, groupId: string ): void => {
    this.planningUpdateQueue$.next({ date, isWorked, groupId });
  }

  calendarItemWeekClick = (dates: Date[], isWorked: boolean, groupId: string): void => {
    dates.forEach(date => {
      this.planningUpdateQueue$.next({ date, isWorked, groupId });
    });
  }

  updatePlanningGestion(date: Date, isWorked: boolean, groupId: string): Observable<IPlanningRH> {
    this.startUpdate();
    if(this.svcPlatform.isMobile) {
      this.currentGroupId = groupId;
      this.updatedDates.set(date, isWorked);
      return of(null)
    } else {
      return this.svcPlanningRH.getPlanning(date).pipe(
        switchMap((planningRH: IPlanningRH) => {
            if (planningRH) {
              this.addAffectationToPlanning(planningRH, date, groupId, isWorked);
              return this.svcPlanningRH.updatePlanning(planningRH);
            } else {
                return this.svcPlanningRH.createPlanning(this.getMondayOfWeek(date)).pipe(
                    switchMap((newPlanningRH: IPlanningRH) => {
                        this.addNewAffectation(newPlanningRH, groupId, date);
                        return this.svcPlanningRH.updatePlanning(newPlanningRH);
                    })
                );
            }
        }),
        finalize(() => this.finishUpdate())
      )
    }
	}

  private addAffectationToPlanning(planningRH: IPlanningRH, date: Date, groupId: string, isWorked: boolean): void {
		const indexOfMatchingDate = planningRH.affectations.findIndex((affectation) => {
			if(affectation.groupId != groupId) return false;
			const dateAffectation = new Date(affectation.date).toLocaleDateString();
			return dateAffectation === date.toLocaleDateString();
		});
		if (isWorked) {
			if (indexOfMatchingDate !== -1) {
				planningRH.affectations[indexOfMatchingDate].contactIds.push(this.userSelected._id);
			} else {
				this.addNewAffectation(planningRH, groupId, date);
			}
		} else {
			planningRH.affectations[indexOfMatchingDate].contactIds = planningRH.affectations[indexOfMatchingDate].contactIds.filter(
				(id) => id !== this.userSelected._id
			);
		}
	}

	private addNewAffectation(planningRH: IPlanningRH, groupId: string, date: Date) {
		planningRH.affectations.push({
			slotId: "jour",
			groupId: groupId,
			date: date,
			contactIds: [this.userSelected._id]
		});
	}

  private executeAfterUpdates(updateStateFn?: () => void): Observable<any> {
    this.svcLoader.showLoader("Sauvegarde du planning en cours...");
    const run = () => {
      if(updateStateFn) updateStateFn();
      return this.updateCalendar().pipe(
        tap(() => {
          this.svcLoader.hideLoader();
        })
      );
    };

    if(this.svcPlatform.isMobile && this.hasPendingUpdates()){
      const firstDayOfMonth = new Date(this.year1, this.month1, 1);
      const lastDayOfMonth = new Date(this.year1, this.month1, DateHelper.getMonthAmountOfDays(firstDayOfMonth));
      return this.svcPlanningRH.getPlannings(firstDayOfMonth, lastDayOfMonth).pipe(
        concatMap(planningsRH =>
          from(this.updatedDates.entries()).pipe(
            concatMap(([date, isWorked]) => {
              const planningRH = PlanningRHService.getMatchingPlanning(date, planningsRH);
              if (planningRH) {
                this.addAffectationToPlanning(planningRH, date, this.currentGroupId, isWorked);
                return of(null);
              } else {
                return this.svcPlanningRH.createPlanning(this.getMondayOfWeek(date)).pipe(
                  tap(newPlanningRH => {
                    planningsRH.push(newPlanningRH);
                    this.addNewAffectation(newPlanningRH, this.currentGroupId, date);
                  })
                );
              }
            }),
            toArray(),
            concatMap(() => this.svcPlanningRH.updatePlannings(planningsRH)),
            concatMap(() => {
              const dbId = ArrayHelper.getFirstElement(this.svcStore.getDatabasesIdsByRole(EDatabaseRole.workspace));
              return this.svcStore.replicateToLocal(dbId, {
                since: '0',
                selector: {
                  _id: {
                    $in: planningsRH.map((planning) => planning._id),
                  }
                }
              })  
            })
          )
        ),
        tap(() => {
          this.pendingUpdatesCount = 0;
          this.updatedDates.clear();
        }),
        mergeMap(run),
        takeUntil(this.destroyed$),
        catchError(() => {
          this.svcLoader.hideLoader();
          this.svcSnackbar.showToast(
              "error",
              "bottom center",
              "Une erreur a eu lieu pendant la sauvegarde du planning.",
              null,
              null,
              null,
              "7000"
            );          
            return this.updateCalendar();
        })
      );

    }else{
      if (this.hasPendingUpdates()) {
        return this.planningUpdateCompleted$.pipe(
          take(1),
          takeUntil(this.destroyed$),
          mergeMap(run)
        );
      } else {
        return run().pipe(
          takeUntil(this.destroyed$),
        );
      }
    }
  }
  
  private updateCalendar(): Observable<any> {
    return this.getCalendarPlanning(this.month1, this.year1, this.month2, this.year2).pipe(
      tap(planning => {
        this.planningCalendar = planning
      })
    );
  }

  public setContactSelectedList = (user: IContact): void => {
    this.executeAfterUpdates(() => {
      this.userSelected = user;
      this.showUserName = true;
    }).subscribe();
  };
  
  public previousMonth(): void {
    this.executeAfterUpdates(() => {
      this.month1 = (this.month1 === 0) ? 11 : this.month1 - 1;
      this.year1 = (this.month1 === 11) ? this.year1 - 1 : this.year1;

      this.month2 = (this.month2 === 0) ? 11 : this.month2 - 1;
      this.year2 = (this.month2 === 11) ? this.year2 - 1 : this.year2;
    }).subscribe();
  }
  
  public nextMonth(): void {
    this.executeAfterUpdates(() => {
      this.month1 = (this.month1 === 11) ? 0 : this.month1 + 1;
      this.year1 = (this.month1 === 0) ? this.year1 + 1 : this.year1;

      this.month2 = (this.month2 === 11) ? 0 : this.month2 + 1;
      this.year2 = (this.month2 === 0) ? this.year2 + 1 : this.year2;
    }).subscribe();
  }

  private getCalendarPlanning(moisDebut: number, anneeDebut: number, moisFin: number, anneeFin: number): Observable<Map<string, IAffectation[]>> {
    let dateDebut: Date = new Date(anneeDebut, moisDebut, 1);
    let dateFin: Date = new Date(anneeFin, moisFin + 1, 0);

    return this.svcPlanningRH.getPlannings(dateDebut, dateFin).pipe(
      concatMap((planningRh: IPlanningRH[]) => planningRh),
      reduce((tab: Map<string, IAffectation[]>, planningRh: IPlanningRH) => {
        for (const affectation of planningRh.affectations) {
          if (affectation.contactIds && affectation.contactIds.length > 0) {
            const groupId = affectation.groupId;
            if (this.secteur === groupId) {
              if (!tab.has(groupId)) {
                tab.set(groupId, []);
              }
              tab.get(groupId)!.push(affectation);
            }
          }
        }
        return tab;
      }, new Map<string, IAffectation[]>())
    );
  }

  public getMondayOfWeek(date: Date): Date {
    const currentDate = new Date(date);
    const day = currentDate.getDay();
    const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(currentDate.setDate(diff));
    return monday;
  }
}
